import { DeliveryEstimateSelector } from "@redotech/redo-conversion-core-common/conversion/storefront-delivery-estimate";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import { useEffect, useRef, useState } from "react";
import { useLeakyDebounce } from "../../utils/use-leaky-debounce";
import { RedoDeliveryEstimateContainerElement } from "./element";

export interface DeliveryEstimatePortal {
  foundSelector: string;
  portal: RedoDeliveryEstimateContainerElement;
}

const positionMap: Record<InsertionPosition, InsertPosition> = {
  [InsertionPosition.BEFORE]: "beforebegin",
  [InsertionPosition.AFTER]: "afterend",
  [InsertionPosition.PREPEND]: "afterbegin",
  [InsertionPosition.APPEND]: "beforeend",
};

export function useDeliveryEstimatePortals({
  selectorConfigs,
  containerId,
}: {
  selectorConfigs: DeliveryEstimateSelector[];
  containerId: string;
}): DeliveryEstimatePortal[] {
  const [tick, setTick] = useState(Symbol());
  const debouncedTick = useLeakyDebounce(tick, { outputHz: 4 });

  useEffect(() => {
    const observer = new MutationObserver(() => setTick(Symbol()));
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const trackedRef = useRef(
    new Map<Element, RedoDeliveryEstimateContainerElement>(),
  );
  const [portals, setPortals] = useState<DeliveryEstimatePortal[]>([]);

  useEffect(() => {
    const tracked = trackedRef.current;

    if (selectorConfigs.length === 0) {
      if (tracked.size > 0) {
        for (const element of tracked.values()) {
          element.remove();
        }
        tracked.clear();
        setPortals([]);
      }
      return;
    }

    RedoDeliveryEstimateContainerElement.register();

    for (const [target, element] of tracked) {
      if (!target.isConnected || !element.isConnected) {
        element.remove();
        tracked.delete(target);
      }
    }

    const next: DeliveryEstimatePortal[] = [];
    for (const config of selectorConfigs) {
      for (const target of document.querySelectorAll(config.selector)) {
        let element = tracked.get(target);
        if (!element) {
          element = document.createElement(
            RedoDeliveryEstimateContainerElement.TAG,
          ) as RedoDeliveryEstimateContainerElement;
          element.id = containerId;
          element.style.setProperty("display", "unset", "important");
          target.insertAdjacentElement(
            positionMap[config.insertPosition],
            element,
          );
          tracked.set(target, element);
        }
        next.push({ foundSelector: config.selector, portal: element });
      }
    }

    setPortals((prev) => (samePortals(prev, next) ? prev : next));
  }, [selectorConfigs, containerId, debouncedTick]);

  useEffect(
    () => () => {
      for (const element of trackedRef.current.values()) {
        element.remove();
      }
      trackedRef.current.clear();
    },
    [],
  );

  return portals;
}

function samePortals(
  a: DeliveryEstimatePortal[],
  b: DeliveryEstimatePortal[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (const [i, element] of a.entries()) {
    const other = b[i]!;
    if (
      element.portal !== other.portal ||
      element.foundSelector !== other.foundSelector
    ) {
      return false;
    }
  }
  return true;
}
